// src/pages/FlightForm.js
//
// Single form for logging a new flight (/add-flight) and editing an existing
// one (/edit-flight/:id). Mode is decided by the presence of the :id param.

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, X, Plane, Search, Plus, User, Clock, Users, Navigation, FileText, Target, Settings, Eye, EyeOff, ArrowLeftRight } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import AddAircraftModal from '../components/AddAircraftModal';
import AddCrewModal from '../components/AddCrewModal';
import AcarsPhotoScanner from '../components/AcarsPhotoScanner';
import {
  getAuthHeaders,
  convertTimeToMinutes,
  convertTimeToDatetime,
  calculateBlockDiff,
  durationAcrossTimezones,
  minutesToHours,
  getAirportTimezone,
  getAirportDistance,
  calculateNightTime,
  determineTakeoffLandingNight,
  classifyAircraft,
  applyAircraftCategoryTimes
} from '../utils/flightCalculations';

const DEFAULT_FIELD_SETTINGS = [
  { field_key: 'date', field_label: 'Date', field_type: 'date', field_group: 'basic', display_order: 1, is_visible: true, is_required: true },
  { field_key: 'flight_number', field_label: 'Flight Number', field_type: 'text', field_group: 'basic', display_order: 2, is_visible: true, is_required: false },
  { field_key: 'aircraft_id', field_label: 'Aircraft ID', field_type: 'text', field_group: 'basic', display_order: 3, is_visible: true, is_required: true },
  { field_key: 'aircraft_type', field_label: 'Aircraft Type', field_type: 'text', field_group: 'basic', display_order: 4, is_visible: true, is_required: true },
  { field_key: 'departure_airport', field_label: 'Departure Airport', field_type: 'text', field_group: 'basic', display_order: 5, is_visible: true, is_required: true },
  { field_key: 'arrival_airport', field_label: 'Arrival Airport', field_type: 'text', field_group: 'basic', display_order: 6, is_visible: true, is_required: true },
  { field_key: 'scheduled_out', field_label: 'Scheduled Out', field_type: 'time', field_group: 'scheduled_times', display_order: 10, is_visible: true, is_required: false },
  { field_key: 'scheduled_in', field_label: 'Scheduled In', field_type: 'time', field_group: 'scheduled_times', display_order: 11, is_visible: true, is_required: false },
  { field_key: 'scheduled_block', field_label: 'Scheduled Block', field_type: 'number', field_group: 'scheduled_times', display_order: 12, is_visible: true, is_required: false },
  { field_key: 'block_diff', field_label: 'Block Diff', field_type: 'number', field_group: 'scheduled_times', display_order: 13, is_visible: true, is_required: false },
  { field_key: 'actual_out', field_label: 'Out Time', field_type: 'time', field_group: 'actual_times', display_order: 20, is_visible: true, is_required: false },
  { field_key: 'actual_off', field_label: 'Off Time', field_type: 'time', field_group: 'actual_times', display_order: 21, is_visible: true, is_required: false },
  { field_key: 'actual_on', field_label: 'On Time', field_type: 'time', field_group: 'actual_times', display_order: 22, is_visible: true, is_required: false },
  { field_key: 'actual_in', field_label: 'In Time', field_type: 'time', field_group: 'actual_times', display_order: 23, is_visible: true, is_required: false },
  { field_key: 'total_time', field_label: 'Total Time', field_type: 'number', field_group: 'flight_times', display_order: 30, is_visible: true, is_required: false },
  { field_key: 'pic_time', field_label: 'PIC Time', field_type: 'number', field_group: 'flight_times', display_order: 31, is_visible: true, is_required: false },
  { field_key: 'sic_time', field_label: 'SIC Time', field_type: 'number', field_group: 'flight_times', display_order: 32, is_visible: true, is_required: false },
  { field_key: 'night_time', field_label: 'Night Time', field_type: 'number', field_group: 'flight_times', display_order: 33, is_visible: true, is_required: false },
  { field_key: 'cross_country_time', field_label: 'Cross Country Time', field_type: 'number', field_group: 'flight_times', display_order: 34, is_visible: true, is_required: false },
  { field_key: 'actual_instrument', field_label: 'Actual IMC', field_type: 'number', field_group: 'flight_times', display_order: 35, is_visible: true, is_required: false },
  { field_key: 'simulated_instrument', field_label: 'Simulated Instrument', field_type: 'number', field_group: 'flight_times', display_order: 36, is_visible: true, is_required: false },
  { field_key: 'simulator', field_label: 'SIM/FTD Time', field_type: 'number', field_group: 'flight_times', display_order: 37, is_visible: true, is_required: false },
  { field_key: 'amel_time', field_label: 'Multi-Engine Time', field_type: 'number', field_group: 'aircraft_types', display_order: 40, is_visible: true, is_required: false },
  { field_key: 'turbine_time', field_label: 'Turbine Time', field_type: 'number', field_group: 'aircraft_types', display_order: 41, is_visible: true, is_required: false },
  { field_key: 'turbo_prop_time', field_label: 'Turbo-Prop Time', field_type: 'number', field_group: 'aircraft_types', display_order: 42, is_visible: true, is_required: false },
  { field_key: 'turbo_fan_time', field_label: 'Turbo-Fan Time', field_type: 'number', field_group: 'aircraft_types', display_order: 43, is_visible: true, is_required: false },
  { field_key: 'asel_time', field_label: 'ASEL Time', field_type: 'number', field_group: 'aircraft_types', display_order: 44, is_visible: true, is_required: false },
  { field_key: 'ases_time', field_label: 'ASES Time', field_type: 'number', field_group: 'aircraft_types', display_order: 45, is_visible: false, is_required: false },
  { field_key: 'ames_time', field_label: 'AMES Time', field_type: 'number', field_group: 'aircraft_types', display_order: 46, is_visible: false, is_required: false },
  { field_key: 'dual_received', field_label: 'Dual Received', field_type: 'number', field_group: 'aircraft_types', display_order: 47, is_visible: true, is_required: false },
  { field_key: 'dual_given', field_label: 'Dual Given', field_type: 'number', field_group: 'aircraft_types', display_order: 48, is_visible: true, is_required: false },
  { field_key: 'day_takeoffs', field_label: 'Takeoffs Day', field_type: 'number', field_group: 'landings', display_order: 50, is_visible: true, is_required: false },
  { field_key: 'day_landings', field_label: 'Landings Day', field_type: 'number', field_group: 'landings', display_order: 51, is_visible: true, is_required: false },
  { field_key: 'night_takeoffs', field_label: 'Takeoffs Night', field_type: 'number', field_group: 'landings', display_order: 52, is_visible: true, is_required: false },
  { field_key: 'night_landings', field_label: 'Landings Night', field_type: 'number', field_group: 'landings', display_order: 53, is_visible: true, is_required: false },
  { field_key: 'remarks', field_label: 'Remarks', field_type: 'textarea', field_group: 'landings', display_order: 54, is_visible: true, is_required: false },
  { field_key: 'approach_1_number', field_label: 'Approach 1 Number', field_type: 'number', field_group: 'approaches', display_order: 60, is_visible: true, is_required: false },
  { field_key: 'approach_1_type', field_label: 'Approach 1 Type', field_type: 'text', field_group: 'approaches', display_order: 61, is_visible: true, is_required: false },
  { field_key: 'approach_1_runway', field_label: 'Approach 1 Runway', field_type: 'text', field_group: 'approaches', display_order: 62, is_visible: true, is_required: false },
  { field_key: 'cat_iii', field_label: 'Cat III Approach?', field_type: 'checkbox', field_group: 'approaches', display_order: 63, is_visible: true, is_required: false },
  { field_key: 'approach_2_number', field_label: 'Approach 2 Number', field_type: 'number', field_group: 'approaches', display_order: 64, is_visible: true, is_required: false },
  { field_key: 'approach_2_type', field_label: 'Approach 2 Type', field_type: 'text', field_group: 'approaches', display_order: 65, is_visible: true, is_required: false },
  { field_key: 'approach_2_runway', field_label: 'Approach 2 Runway', field_type: 'text', field_group: 'approaches', display_order: 66, is_visible: true, is_required: false },
  { field_key: 'pic_crew', field_label: 'PIC/Captain Crew', field_type: 'text', field_group: 'crew', display_order: 70, is_visible: true, is_required: false },
  { field_key: 'sic_crew', field_label: 'SIC/P2 Crew', field_type: 'text', field_group: 'crew', display_order: 71, is_visible: true, is_required: false },
  { field_key: 'pilot_flying', field_label: 'Pilot Flying', field_type: 'checkbox', field_group: 'crew', display_order: 72, is_visible: true, is_required: false }
];

// Fields recalculated from Out/In — cleared when either time is removed
const AUTO_CLEARED_FIELDS = [
  'total_time', 'block_diff', 'pic_time', 'sic_time', 'night_time', 'cross_country_time',
  'actual_instrument', 'simulated_instrument', 'simulator', 'amel_time', 'turbine_time',
  'turbo_fan_time', 'turbo_prop_time', 'asel_time', 'ases_time', 'ames_time',
  'dual_received', 'dual_given'
];

const UTC_FIELD_MAP = {
  actual_out: 'utc_out',
  actual_off: 'utc_off',
  actual_on: 'utc_on',
  actual_in: 'utc_in'
};

const inputFieldClasses = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500";
const labelClasses = "block text-sm font-medium text-gray-700 mb-2";
const cardClasses = "bg-white p-8 shadow-lg rounded-xl border border-gray-200";
const cardTitleClasses = "text-2xl font-semibold text-gray-800 mb-6 border-b pb-3 flex items-center";

// Parse the flight's date from the API into YYYY-MM-DD without timezone shifts
const convertFlightDate = (dateValue) => {
  if (!dateValue) return '';

  try {
    if (typeof dateValue === 'string') {
      let parsedDate;
      if (dateValue.includes('T') || dateValue.includes(' ')) {
        // Database datetime strings like "2025-12-04T22:51:00" are local dates —
        // take the date part directly to avoid a UTC shift
        const datePart = dateValue.includes('T') ? dateValue.split('T')[0] : dateValue.split(' ')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        parsedDate = new Date(year, month - 1, day);
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        // Plain dates too — new Date('YYYY-MM-DD') would parse as UTC midnight
        // and show the previous day in US timezones
        return dateValue;
      } else {
        parsedDate = new Date(dateValue);
      }
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'yyyy-MM-dd');
      }
    }

    if (typeof dateValue === 'number') {
      // Excel serial date
      if (dateValue < 1 || dateValue > 2958465) {
        console.warn('Invalid Excel serial date:', dateValue);
        return '';
      }
      const jsDate = new Date((dateValue - 25569) * 86400 * 1000);
      return isNaN(jsDate.getTime()) ? '' : format(jsDate, 'yyyy-MM-dd');
    }

    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      return format(dateValue, 'yyyy-MM-dd');
    }

    console.warn('Unhandled date format:', dateValue, typeof dateValue);
    return '';
  } catch (error) {
    console.error('Error converting date:', error, dateValue);
    return '';
  }
};

// Form values the auto-calculations read. When editing, the saved flight's
// derived values (PIC/SIC, night, takeoffs/landings…) are kept until one of
// these is changed.
const CALCULATION_INPUTS = [
  'actual_out', 'actual_in', 'scheduled_out', 'scheduled_in',
  'departure_airport', 'arrival_airport', 'aircraft_id', 'pilot_flying'
];

const toPaddedTime = (value) => (value ? value.toString().padStart(4, '0') : null);
const toIntOrNull = (value) => (value ? parseInt(value, 10) : null);

const FlightForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  // /add-flight?returnOf=<id> pre-fills the reverse of that flight
  const [searchParams] = useSearchParams();
  const returnOf = isEdit ? null : searchParams.get('returnOf');

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [userId, setUserId] = useState(null);
  const [showAddAircraftModal, setShowAddAircraftModal] = useState(false);
  const [showAddCrewModal, setShowAddCrewModal] = useState(false);
  const [crewModalTargetField, setCrewModalTargetField] = useState(null);

  // Trip assignment (new flights only — edits keep their existing trip)
  const [availableTrips, setAvailableTrips] = useState([]);
  const [tripOption, setTripOption] = useState('none'); // 'none', 'existing', 'new'
  const [loadingTrips, setLoadingTrips] = useState(false);

  // Popup states
  const [aircraftPopup, setAircraftPopup] = useState(false);
  const [crewPopup, setCrewPopup] = useState({ open: false, field: null });

  // Autocomplete states
  const [aircraftSuggestions, setAircraftSuggestions] = useState([]);
  const [crewSuggestions, setCrewSuggestions] = useState([]);
  const [searchingAircraft, setSearchingAircraft] = useState(false);
  const [searchingCrew, setSearchingCrew] = useState(false);

  // Field configuration states
  const [fieldSettings, setFieldSettings] = useState([]);
  const [loadingFieldSettings, setLoadingFieldSettings] = useState(false);

  // Inline field-visibility customization
  const [isCustomizingFields, setIsCustomizingFields] = useState(false);
  const [tempFieldSettings, setTempFieldSettings] = useState([]);

  // Category flags of the selected aircraft, and whether the PIC crew member
  // holds PIC — both drive the auto-filled time fields when Out/In change
  const aircraftInfoRef = useRef(null);
  const picCrewIsPicRef = useRef(false);

  // Edit mode: calculation inputs as loaded, and whether the user has since
  // changed any of them
  const loadedInputsRef = useRef(null);
  const inputsChangedRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues
  } = useForm({
    defaultValues: isEdit ? {} : {
      date: format(new Date(), 'yyyy-MM-dd'),
      pilot_flying: false
    }
  });

  const actualOut = watch('actual_out');
  const actualIn = watch('actual_in');
  const scheduledOut = watch('scheduled_out');
  const scheduledIn = watch('scheduled_in');
  const pilotFlying = watch('pilot_flying');
  const aircraftId = watch('aircraft_id');
  const departureAirport = watch('departure_airport');
  const arrivalAirport = watch('arrival_airport');

  // True while an existing flight is loading, and afterwards until the user
  // changes an input — so opening a flight never overwrites its saved values
  const keepSavedValues = useCallback(() => {
    if (!isEdit || inputsChangedRef.current) return false;
    if (!loadedInputsRef.current) return true;
    const values = getValues();
    const unchanged = CALCULATION_INPUTS.every(
      key => String(values[key] ?? '') === loadedInputsRef.current[key]
    );
    if (!unchanged) inputsChangedRef.current = true;
    return unchanged;
  }, [isEdit, getValues]);

  // ─── Field visibility customization ────────────────────────────────────────

  const enterCustomizeMode = () => {
    if (fieldSettings.length === 0) {
      setFieldSettings(DEFAULT_FIELD_SETTINGS);
      setTempFieldSettings(DEFAULT_FIELD_SETTINGS);
    } else {
      setTempFieldSettings([...fieldSettings]);
    }
    setIsCustomizingFields(true);
  };

  const exitCustomizeMode = () => {
    setTempFieldSettings([]);
    setIsCustomizingFields(false);
  };

  const toggleFieldVisibility = (fieldKey) => {
    setTempFieldSettings(prev =>
      prev.map(field =>
        field.field_key === fieldKey
          ? { ...field, is_visible: !field.is_visible }
          : field
      )
    );
  };

  const saveFieldSettings = async () => {
    try {
      setLoadingFieldSettings(true);
      await axios.put(`/api/field-settings/${userId}`, { settings: tempFieldSettings }, getAuthHeaders());
      setFieldSettings([...tempFieldSettings]);
      setIsCustomizingFields(false);
      setTempFieldSettings([]);
      toast.success('Field settings saved successfully');
    } catch (error) {
      console.error('Error saving field settings:', error);
      toast.error('Failed to save field settings');
    } finally {
      setLoadingFieldSettings(false);
    }
  };

  const isFieldVisible = (fieldKey) => {
    if (isCustomizingFields) return true; // Show all fields while customizing
    const fieldSetting = fieldSettings.find(f => f.field_key === fieldKey);
    return fieldSetting ? fieldSetting.is_visible : true;
  };

  // Wrap a field with the show/hide eye toggle used while customizing
  const renderFieldWithEye = (fieldKey, children) => {
    if (!isCustomizingFields) {
      return <div data-field-key={fieldKey}>{children}</div>;
    }

    const fieldSetting = tempFieldSettings.find(f => f.field_key === fieldKey);
    const isVisible = fieldSetting ? fieldSetting.is_visible : true;

    return (
      <div data-field-key={fieldKey} className="relative group">
        {children}
        <button
          type="button"
          onClick={() => toggleFieldVisibility(fieldKey)}
          className={`absolute top-2 right-2 p-1 rounded-md transition-all duration-200 z-10 opacity-100 ${
            isVisible
              ? 'text-green-600 hover:bg-green-100 hover:text-green-700'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
          title={isVisible ? 'Hide this field' : 'Show this field'}
        >
          {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
        {!isVisible && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 rounded-lg flex items-center justify-center">
            <span className="text-gray-500 text-sm font-medium">Hidden</span>
          </div>
        )}
      </div>
    );
  };

  const field = (fieldKey, children) =>
    isFieldVisible(fieldKey) && renderFieldWithEye(fieldKey, children);

  const numberField = (fieldKey, label, { placeholder = '0.0', step = '0.1' } = {}) =>
    field(fieldKey,
      <div>
        <label htmlFor={fieldKey} className={labelClasses}>{label}</label>
        <input type="number" step={step} id={fieldKey} {...register(fieldKey)} className={inputFieldClasses} placeholder={placeholder} />
      </div>
    );

  // ─── Loading ───────────────────────────────────────────────────────────────

  // Get user ID from token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.userId || payload.id);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    const loadFieldSettings = async () => {
      setLoadingFieldSettings(true);
      try {
        const response = await axios.get(`/api/field-settings/${userId}`, getAuthHeaders());
        setFieldSettings(response.data);
      } catch (error) {
        console.error('Error loading field settings:', error);
        setFieldSettings(DEFAULT_FIELD_SETTINGS);
      } finally {
        setLoadingFieldSettings(false);
      }
    };
    loadFieldSettings();
  }, [userId]);

  useEffect(() => {
    if (isEdit) return;
    const loadTrips = async () => {
      setLoadingTrips(true);
      try {
        const response = await axios.get('/api/trips', getAuthHeaders());
        setAvailableTrips(response.data.trips || []);
      } catch (error) {
        console.error('Error loading trips:', error);
        toast.error('Failed to load trips');
      } finally {
        setLoadingTrips(false);
      }
    };
    loadTrips();
  }, [isEdit]);

  // ─── Calculations ──────────────────────────────────────────────────────────

  // Calculate total time (and everything derived from it) when Out/In change
  useEffect(() => {
    // PIC vs SIC is decided by the PIC crew member's pic flag only
    const applyPicSic = (totalHours) => {
      if (picCrewIsPicRef.current) {
        setValue('pic_time', totalHours);
        setValue('sic_time', 0);
      } else {
        setValue('sic_time', totalHours);
        setValue('pic_time', 0);
      }
    };

    const applyAircraftTimes = (totalHours) => {
      if (aircraftInfoRef.current) {
        applyAircraftCategoryTimes(setValue, aircraftInfoRef.current, totalHours);
      }
    };

    // Cross-country = more than 50 NM between airports
    const applyCrossCountry = (flightDistance, totalHours) => {
      if (flightDistance === null) return;
      setValue('cross_country_time', flightDistance > 50 ? totalHours : 0);
    };

    const calculateTotalTime = async () => {
      if (!actualOut || !actualIn) {
        AUTO_CLEARED_FIELDS.forEach(key => setValue(key, ''));
        return;
      }

      const blockDiff = calculateBlockDiff(scheduledOut, scheduledIn, actualOut, actualIn);
      setValue('block_diff', blockDiff !== null ? blockDiff : '');

      const flightDate = getValues('date') || format(new Date(), 'yyyy-MM-dd');

      try {
        let departureOffset = 0;
        let arrivalOffset = 0;
        let flightDistance = null;

        if (departureAirport) {
          departureOffset = await getAirportTimezone(departureAirport, flightDate);
        }
        if (arrivalAirport) {
          arrivalOffset = await getAirportTimezone(arrivalAirport, flightDate);
          if (departureAirport && departureAirport !== arrivalAirport) {
            const distanceData = await getAirportDistance(departureAirport, arrivalAirport);
            if (distanceData) flightDistance = distanceData.distanceNM;
          }
        }

        const outMinutes = convertTimeToMinutes(actualOut);
        const inMinutes = convertTimeToMinutes(actualIn);

        if (outMinutes === 0 && inMinutes === 0) {
          console.log('Invalid actual time format');
          return;
        }

        let totalMinutes;
        if (departureAirport && (departureOffset !== 0 || arrivalOffset !== 0)) {
          ({ totalMinutes } = durationAcrossTimezones(outMinutes, inMinutes, departureOffset, arrivalOffset));
        } else {
          // No timezone data — simple local time difference
          totalMinutes = inMinutes - outMinutes;
          if (totalMinutes < 0) totalMinutes += 24 * 60;
        }

        const totalHours = minutesToHours(totalMinutes);
        setValue('total_time', totalHours);

        // Night time using civil twilight
        if (departureAirport) {
          const nightHours = await calculateNightTime(
            departureAirport,
            arrivalAirport || departureAirport,
            flightDate,
            actualOut,
            actualIn,
            departureOffset,
            arrivalOffset
          );
          if (nightHours !== null) {
            setValue('night_time', nightHours);
          }
        }

        applyPicSic(totalHours);
        applyCrossCountry(flightDistance, totalHours);
        applyAircraftTimes(totalHours);
      } catch (error) {
        console.error('Error in timezone-aware actual time calculation:', error);
        // Fallback to simple local time calculation
        let totalMinutes = convertTimeToMinutes(actualIn) - convertTimeToMinutes(actualOut);
        if (totalMinutes < 0) totalMinutes += 24 * 60;

        const totalHours = minutesToHours(totalMinutes);
        setValue('total_time', totalHours);
        applyPicSic(totalHours);
        applyAircraftTimes(totalHours);

        if (departureAirport && arrivalAirport && departureAirport !== arrivalAirport) {
          getAirportDistance(departureAirport, arrivalAirport)
            .then(distanceData => applyCrossCountry(distanceData ? distanceData.distanceNM : null, totalHours))
            .catch(err => console.warn('Error calculating distance in fallback:', err));
        }
      }
    };

    if ((actualOut || actualIn) && !keepSavedValues()) {
      calculateTotalTime();
    }
  }, [actualOut, actualIn, scheduledOut, scheduledIn, aircraftId, departureAirport, arrivalAirport, setValue, getValues, keepSavedValues]);

  // Auto-populate takeoffs/landings when pilot_flying changes
  useEffect(() => {
    const autoPopulateTakeoffsLandings = async () => {
      if (!pilotFlying) {
        // Pilot monitoring — no takeoffs or landings
        setValue('day_takeoffs', 0);
        setValue('day_landings', 0);
        setValue('night_takeoffs', 0);
        setValue('night_landings', 0);
        return;
      }

      // Takeoff uses actual_out at departure; landing uses actual_in at arrival
      const depAirport = getValues('departure_airport');
      const { takeoffIsNight, landingIsNight } = await determineTakeoffLandingNight({
        depAirport,
        arrAirport: getValues('arrival_airport') || depAirport,
        flightDate: getValues('date') || format(new Date(), 'yyyy-MM-dd'),
        outTime: getValues('actual_out'),
        inTime: getValues('actual_in')
      });

      setValue('day_takeoffs', takeoffIsNight ? 0 : 1);
      setValue('night_takeoffs', takeoffIsNight ? 1 : 0);
      setValue('day_landings', landingIsNight ? 0 : 1);
      setValue('night_landings', landingIsNight ? 1 : 0);
    };

    if (!keepSavedValues()) {
      autoPopulateTakeoffsLandings();
    }
  }, [pilotFlying, actualOut, actualIn, departureAirport, arrivalAirport, setValue, getValues, keepSavedValues]);

  // Calculate scheduled block time when scheduled times change
  useEffect(() => {
    if (keepSavedValues()) return;

    if (!scheduledOut || !scheduledIn) {
      setValue('scheduled_block', '');
      return;
    }

    const simpleBlock = () => {
      const outTime = convertTimeToMinutes(scheduledOut);
      const inTime = convertTimeToMinutes(scheduledIn);
      if (outTime === 0 && inTime === 0) {
        console.log('Invalid scheduled time format');
        return null;
      }
      let totalMinutes = inTime - outTime;
      if (totalMinutes < 0) totalMinutes += 24 * 60; // overnight
      return minutesToHours(totalMinutes);
    };

    // Debounce to avoid recalculating on every keystroke. Airports are read
    // when it fires rather than watched, so this only runs for time changes.
    const timeoutId = setTimeout(async () => {
      const departureAirport = getValues('departure_airport');
      const arrivalAirport = getValues('arrival_airport');
      if (!departureAirport) {
        const hours = simpleBlock();
        if (hours !== null) setValue('scheduled_block', hours);
        return;
      }

      try {
        const flightDate = getValues('date') || format(new Date(), 'yyyy-MM-dd');
        const departureOffset = await getAirportTimezone(departureAirport, flightDate);
        const arrivalOffset = await getAirportTimezone(arrivalAirport || departureAirport, flightDate);

        const outMinutes = convertTimeToMinutes(scheduledOut);
        const inMinutes = convertTimeToMinutes(scheduledIn);
        if (outMinutes === 0 && inMinutes === 0) {
          console.log('Invalid time format, skipping timezone calculation');
          return;
        }

        const { totalMinutes } = durationAcrossTimezones(outMinutes, inMinutes, departureOffset, arrivalOffset);
        const totalHours = minutesToHours(totalMinutes);
        setValue('scheduled_block', totalHours);

        if (!isEdit) {
          const departureTZ = departureOffset >= 0 ? `+${departureOffset}` : `${departureOffset}`;
          const arrivalTZ = arrivalOffset >= 0 ? `+${arrivalOffset}` : `${arrivalOffset}`;
          toast.success(`${totalHours}h (UTC${departureTZ} → UTC${arrivalTZ})`, { duration: 2000 });
        }
      } catch (error) {
        console.error('Error calculating timezone-aware scheduled block:', error);
        const hours = simpleBlock();
        if (hours !== null) {
          setValue('scheduled_block', hours);
          if (!isEdit) toast.success(`${hours}h (local time only)`, { duration: 1500 });
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [scheduledOut, scheduledIn, isEdit, setValue, getValues, keepSavedValues]);

  // ─── UTC ↔ local conversion ────────────────────────────────────────────────

  // Offset of the airport relevant to a time field: Out/Off use departure,
  // On/In use arrival (falling back to departure)
  const getOffsetForField = useCallback(async (fieldName) => {
    const values = getValues();
    const isArrivalTime = fieldName === 'actual_on' || fieldName === 'actual_in';
    const airportToUse = isArrivalTime
      ? (values.arrival_airport || values.departure_airport)
      : values.departure_airport;

    if (!airportToUse) {
      console.warn(`No airport selected for ${fieldName} conversion`);
      return null;
    }

    const flightDate = values.date || format(new Date(), 'yyyy-MM-dd');
    return getAirportTimezone(airportToUse, flightDate);
  }, [getValues]);

  const shiftHHMM = (hhmm, offsetHours) => {
    const timeStr = hhmm.toString().padStart(4, '0');
    const hours = parseInt(timeStr.substring(0, 2));
    const minutes = parseInt(timeStr.substring(2));
    if (hours > 23 || minutes > 59) return null;

    let shifted = hours + offsetHours;
    if (shifted >= 24) shifted -= 24;
    else if (shifted < 0) shifted += 24;

    return (Math.floor(shifted) * 100 + minutes).toString().padStart(4, '0');
  };

  // Set a field and mirror it into the DOM input so it shows immediately
  const setFieldAndDom = useCallback((element, fieldName, value) => {
    setValue(fieldName, value);
    if (element) {
      element.value = value;
      element.dispatchEvent(new Event('input', { bubbles: true }));
      element.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [setValue]);

  const convertUTCToLocal = async (fieldName, utcTime) => {
    if (!utcTime || utcTime.length !== 4) {
      console.log('Invalid UTC time provided');
      return;
    }
    try {
      const offset = await getOffsetForField(fieldName);
      if (offset === null) return;
      const localTime = shiftHHMM(utcTime, offset);
      if (localTime === null) {
        console.error(`Invalid UTC time: ${utcTime}`);
        return;
      }
      setFieldAndDom(document.querySelector(`input[name="${fieldName}"]`), fieldName, localTime);
    } catch (error) {
      console.error('UTC to Local conversion error:', error);
    }
  };

  const convertLocalToUTC = useCallback(async (fieldName, localTime) => {
    if (!localTime || localTime.length !== 4) {
      console.log('Invalid local time provided');
      return;
    }
    try {
      const offset = await getOffsetForField(fieldName);
      if (offset === null) return;
      const utcTime = shiftHHMM(localTime, -offset);
      if (utcTime === null) {
        console.error(`Invalid local time: ${localTime}`);
        return;
      }
      const utcField = UTC_FIELD_MAP[fieldName];
      if (utcField) {
        setFieldAndDom(document.getElementById(utcField), utcField, utcTime);
      }
    } catch (error) {
      console.error('Local to UTC conversion error:', error);
    }
  }, [getOffsetForField, setFieldAndDom]);

  const handleAcarsTimesApplied = (times) => {
    const acarsFieldMap = { out: 'actual_out', off: 'actual_off', on: 'actual_on', in: 'actual_in' };
    let appliedCount = 0;

    Object.entries(acarsFieldMap).forEach(([key, localField]) => {
      const value = times[key];
      if (!value) return;
      appliedCount += 1;

      // Same path as typing into the UTC inputs: fill the UTC field, then
      // convert so the local time fields fill in too
      const utcField = UTC_FIELD_MAP[localField];
      setFieldAndDom(document.getElementById(utcField), utcField, value);
      setTimeout(() => convertUTCToLocal(localField, value), 100);
    });

    if (appliedCount > 0) {
      toast.success(`Applied ${appliedCount} time${appliedCount === 1 ? '' : 's'} from photo scan`);
    }
  };

  // ─── Edit mode: load the existing flight ───────────────────────────────────

  // Category flags (multi-engine, turbine…) for the time auto-fill
  const loadAircraftInfo = async (aircraftId) => {
    if (!aircraftId) return;
    try {
      const aircraftResponse = await axios.get(`/api/aircraft/autocomplete?query=${aircraftId}`, getAuthHeaders());
      const matchingAircraft = aircraftResponse.data.find(a => a.aircraft_id === aircraftId);
      if (matchingAircraft) {
        aircraftInfoRef.current = classifyAircraft(matchingAircraft);
      }
    } catch (error) {
      console.error('Error loading aircraft information:', error);
    }
  };

  // Whether the PIC crew member holds PIC — decides PIC vs SIC time
  const loadPicCrewFlag = async (picCrewId) => {
    picCrewIsPicRef.current = false;
    if (!picCrewId) return;
    try {
      const crewResponse = await axios.get(`/api/crew/${picCrewId}`, getAuthHeaders());
      picCrewIsPicRef.current = crewResponse.data?.pic === 1;
    } catch (error) {
      console.error('Error loading PIC crew member:', error);
    }
  };

  const fetchFlight = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await axios.get(`/api/flights/${id}`, getAuthHeaders());
      const flight = response.data;

      Object.keys(flight).forEach(key => {
        if (key === 'date') {
          setValue('date', convertFlightDate(flight[key]));
        } else if (key === 'pilot_flying' || key === 'cat_iii') {
          setValue(key, Boolean(flight[key]));
        } else if (key !== 'approach_1' && key !== 'approach_2' && !key.endsWith('_datetime')) {
          // Skip legacy approach strings and datetime columns; the VARCHAR
          // time columns hold the local times shown in the form
          setValue(key, flight[key] || '');
        }
      });

      // The API JOIN returns pic_crew_name / sic_crew_name alongside the UUIDs
      if (flight.pic_crew_name) setValue('pic_crew_display', flight.pic_crew_name);
      if (flight.sic_crew_name) setValue('sic_crew_display', flight.sic_crew_name);

      // Prefer the canonical aircraft_types.type_code over the free-text column
      if (flight.aircraft_type_lookup) {
        setValue('aircraft_type', flight.aircraft_type_lookup);
      }

      // The PIC crew member's pic flag decides PIC vs SIC when times change
      await loadPicCrewFlag(flight.pic_crew);

      ['scheduled_out', 'scheduled_in', 'actual_out', 'actual_off', 'actual_on', 'actual_in'].forEach(key => {
        if (flight[key]) {
          setValue(key, flight[key].toString().padStart(4, '0'));
        }
      });

      // Legacy rows store approaches as "number;type;runway;airport"
      ['approach_1', 'approach_2'].forEach(key => {
        if (!flight[key]) return;
        const parts = flight[key].split(';');
        if (parts.length >= 3) {
          setValue(`${key}_number`, parts[0] || '');
          setValue(`${key}_type`, parts[1] || '');
          setValue(`${key}_runway`, parts[2] || '');
        }
      });

      // Fill the UTC fields from the loaded local times
      setTimeout(async () => {
        try {
          for (const key of Object.keys(UTC_FIELD_MAP)) {
            if (flight[key]) await convertLocalToUTC(key, flight[key]);
          }
        } catch (error) {
          console.error('Error calculating UTC times during flight load:', error);
        }
      }, 100);

      await loadAircraftInfo(flight.aircraft_id);

      const loadedValues = getValues();
      loadedInputsRef.current = Object.fromEntries(
        CALCULATION_INPUTS.map(key => [key, String(loadedValues[key] ?? '')])
      );
    } catch (error) {
      console.error('Error fetching flight:', error);
      toast.error('Failed to load flight data');
      navigate('/flights');
    } finally {
      setInitialLoading(false);
    }
  }, [id, setValue, getValues, navigate, convertLocalToUTC]);

  // Return leg: same day, aircraft, crew and trip; airports swapped and the
  // pilot flying duty handed over. Times, flight number, approaches and
  // remarks start blank.
  const pendingTripIdRef = useRef(null);

  useEffect(() => {
    if (!returnOf) return;
    const prefillReturnLeg = async () => {
      try {
        const { data: flight } = await axios.get(`/api/flights/${returnOf}`, getAuthHeaders());

        if (flight.date) setValue('date', convertFlightDate(flight.date));
        setValue('aircraft_id', flight.aircraft_id || '');
        setValue('aircraft_type', flight.aircraft_type_lookup || flight.aircraft_type || '');
        setValue('departure_airport', flight.arrival_airport || '');
        setValue('arrival_airport', flight.departure_airport || '');
        setValue('pic_crew', flight.pic_crew || '');
        setValue('pic_crew_display', flight.pic_crew_name || '');
        setValue('sic_crew', flight.sic_crew || '');
        setValue('sic_crew_display', flight.sic_crew_name || '');
        setValue('pilot_flying', !flight.pilot_flying);

        if (flight.trip_id) {
          pendingTripIdRef.current = flight.trip_id;
          setTripOption('existing');
        }

        await Promise.all([loadAircraftInfo(flight.aircraft_id), loadPicCrewFlag(flight.pic_crew)]);

        toast.success(`Return leg of ${flight.flight_number || 'flight'} ${flight.departure_airport}→${flight.arrival_airport} — add the times`);
      } catch (error) {
        console.error('Error loading flight for return leg:', error);
        toast.error('Couldn\'t load the original flight');
      }
    };
    prefillReturnLeg();
  }, [returnOf, setValue]);

  // Select the original's trip once the trip list has loaded
  useEffect(() => {
    const tripId = pendingTripIdRef.current;
    if (!tripId || tripOption !== 'existing' || loadingTrips) return;
    if (availableTrips.some(t => t.id === tripId)) {
      setValue('existing_trip_id', tripId);
    } else {
      setTripOption('none'); // trip no longer listed — fall back to an individual flight
    }
    pendingTripIdRef.current = null;
  }, [availableTrips, tripOption, loadingTrips, setValue]);

  useEffect(() => {
    if (isEdit) fetchFlight();
  }, [isEdit, fetchFlight]);

  // ─── Aircraft & crew pickers ───────────────────────────────────────────────

  const searchAircraft = async (query) => {
    if (!query || query.length < 1) {
      setAircraftSuggestions([]);
      return;
    }

    setSearchingAircraft(true);
    try {
      const response = await axios.get(`/api/aircraft/autocomplete?query=${query}`, getAuthHeaders());
      setAircraftSuggestions(response.data);
    } catch (error) {
      console.error('Error searching aircraft:', error);
      setAircraftSuggestions([]);
    } finally {
      setSearchingAircraft(false);
    }
  };

  const searchCrew = async (query, fieldName = null) => {
    if (!query || query.length < 1) {
      setCrewSuggestions([]);
      return;
    }

    setSearchingCrew(true);
    try {
      const response = await axios.get(`/api/crew/autocomplete?query=${query}`, getAuthHeaders());
      setCrewSuggestions(response.data);
      if (fieldName) {
        setCrewPopup({ open: false, field: fieldName });
      }
    } catch (error) {
      console.error('Error searching crew:', error);
      setCrewSuggestions([]);
    } finally {
      setSearchingCrew(false);
    }
  };

  const selectAircraft = (aircraft) => {
    setValue('aircraft_id', aircraft.aircraft_id);
    setValue('aircraft_type', aircraft.aircraft_type);
    setAircraftSuggestions([]);
    setAircraftPopup(false);

    aircraftInfoRef.current = classifyAircraft(aircraft);
    applyAircraftCategoryTimes(setValue, aircraftInfoRef.current, getValues('total_time') || 0);
  };

  // Store the crew UUID in the hidden field and the name in the visible one
  const selectCrew = (crew) => {
    const targetField = crewPopup.field;
    if (targetField !== 'pic_crew' && targetField !== 'sic_crew') return;

    setValue(targetField, crew.id);
    setValue(`${targetField}_display`, crew.name);
    if (targetField === 'pic_crew') {
      picCrewIsPicRef.current = crew.pic === 1;
    }
    if (crew.pic === 1) {
      const totalTime = parseFloat(getValues('total_time')) || 0;
      if (totalTime > 0) {
        setValue('pic_time', totalTime);
        setValue('sic_time', 0);
      }
    }
    setCrewSuggestions([]);
    setCrewPopup({ open: false, field: null });
  };

  const handleAircraftAdded = (newAircraft) => {
    setValue('aircraft_id', newAircraft.aircraft_id);
    setValue('aircraft_type', newAircraft.aircraft_type);
    setShowAddAircraftModal(false);
    setAircraftPopup(false);

    aircraftInfoRef.current = classifyAircraft(newAircraft);
    const totalTime = getValues('total_time') || 0;
    if (totalTime > 0) {
      applyAircraftCategoryTimes(setValue, aircraftInfoRef.current, totalTime);
    }

    toast.success(`Aircraft ${newAircraft.aircraft_id} added and selected!`);
  };

  const handleCrewAdded = (newCrew) => {
    if (crewModalTargetField) {
      setValue(crewModalTargetField, newCrew.id);
      setValue(`${crewModalTargetField}_display`, newCrew.name);
      toast.success(`${newCrew.name} added and selected!`);
    }
    setShowAddCrewModal(false);
    setCrewModalTargetField(null);
    setCrewPopup({ open: false, field: null });
  };

  const openCrewModal = (targetField) => {
    setCrewModalTargetField(targetField);
    setShowAddCrewModal(true);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    setLoading(true);

    const flightData = {
      date: data.date || null,
      flight_number: data.flight_number || null,
      aircraft_id: data.aircraft_id || null,
      aircraft_type: data.aircraft_type || null,
      departure_airport: data.departure_airport || null,
      arrival_airport: data.arrival_airport || null,

      scheduled_out: toPaddedTime(data.scheduled_out),
      scheduled_in: toPaddedTime(data.scheduled_in),
      scheduled_block: data.scheduled_block || null,
      block_diff: data.block_diff ? data.block_diff.toString() : null,

      actual_out: toPaddedTime(data.actual_out),
      actual_off: toPaddedTime(data.actual_off),
      actual_on: toPaddedTime(data.actual_on),
      actual_in: toPaddedTime(data.actual_in),

      // Datetime fields for timezone support
      scheduled_out_datetime: convertTimeToDatetime(data.date, data.scheduled_out),
      scheduled_in_datetime: convertTimeToDatetime(data.date, data.scheduled_in),
      actual_out_datetime: convertTimeToDatetime(data.date, data.actual_out),
      actual_off_datetime: convertTimeToDatetime(data.date, data.actual_off),
      actual_on_datetime: convertTimeToDatetime(data.date, data.actual_on),
      actual_in_datetime: convertTimeToDatetime(data.date, data.actual_in),

      total_time: data.total_time || null,
      pic_time: data.pic_time || null,
      sic_time: data.sic_time || null,
      night_time: data.night_time || null,
      cross_country_time: data.cross_country_time || null,
      actual_instrument: data.actual_instrument || null,
      simulated_instrument: data.simulated_instrument || null,
      simulator: data.simulator || null,

      amel_time: data.amel_time || null,
      asel_time: data.asel_time || null,
      ases_time: data.ases_time || null,
      ames_time: data.ames_time || null,
      turbine_time: data.turbine_time || null,
      turbo_prop_time: data.turbo_prop_time || null,
      turbo_fan_time: data.turbo_fan_time || null,
      dual_received: data.dual_received || null,
      dual_given: data.dual_given || null,

      day_takeoffs: toIntOrNull(data.day_takeoffs),
      day_landings: toIntOrNull(data.day_landings),
      night_takeoffs: toIntOrNull(data.night_takeoffs),
      night_landings: toIntOrNull(data.night_landings),
      remarks: data.remarks || null,

      approach_1_number: data.approach_1_number || null,
      approach_1_type: data.approach_1_type || null,
      approach_1_runway: data.approach_1_runway || null,
      approach_2_number: data.approach_2_number || null,
      approach_2_type: data.approach_2_type || null,
      approach_2_runway: data.approach_2_runway || null,
      cat_iii: data.cat_iii || false,

      // Crew — send UUID (crew.id), never the display name
      pic_crew: data.pic_crew || null,
      sic_crew: data.sic_crew || null,
      pilot_flying: data.pilot_flying || false,

      on_duty: data.on_duty || null,
      off_duty: data.off_duty || null
    };

    try {
      if (isEdit) {
        // Keep the flight in whatever trip it already belongs to
        flightData.trip_id = data.trip_id || null;
        await axios.put(`/api/flights/${id}`, flightData, getAuthHeaders());
        toast.success('Flight log updated successfully!');
      } else {
        flightData.trip_option = tripOption;
        flightData.trip_number = tripOption === 'new' ? data.new_trip_number : null;
        flightData.existing_trip_id = tripOption === 'existing' ? data.existing_trip_id : null;

        const response = await axios.post('/api/flights', flightData, getAuthHeaders());
        toast.success(response.data.trip_id
          ? 'Flight added and assigned to trip successfully!'
          : 'Flight added successfully as individual flight!');
      }
      navigate('/flights');
    } catch (error) {
      console.error('Error saving flight:', error);
      toast.error(error.response?.data?.error || 'Failed to save flight log');
    } finally {
      setLoading(false);
    }
  };

  // ─── Render helpers ────────────────────────────────────────────────────────

  const localTimeField = (fieldKey, label, placeholder) =>
    field(fieldKey,
      <div>
        <label htmlFor={fieldKey} className={labelClasses}>{label} (Local)</label>
        <input
          type="number"
          id={fieldKey}
          {...register(fieldKey)}
          className={inputFieldClasses}
          placeholder={placeholder}
          maxLength="4"
          onChange={(e) => {
            const localValue = e.target.value.replace(/[^0-9]/g, '');
            setValue(fieldKey, localValue);
            if (localValue.length === 4) {
              convertLocalToUTC(fieldKey, localValue);
            }
          }}
        />
        <p className="text-xs text-gray-500 mt-1">Format: HHMM (24hr Local)</p>
      </div>
    );

  const utcTimeField = (localField, label, placeholder) => {
    const utcField = UTC_FIELD_MAP[localField];
    return (
      <div>
        <label htmlFor={utcField} className="block text-sm font-medium text-sky-700 mb-2">{label}</label>
        <input
          type="number"
          id={utcField}
          {...register(utcField)}
          className={`${inputFieldClasses} bg-white`}
          placeholder={placeholder}
          maxLength="4"
          onChange={(e) => {
            const utcValue = e.target.value.replace(/[^0-9]/g, '');
            setValue(utcField, utcValue);
            if (utcValue.length === 4) {
              // Small delay so the other form values are current
              setTimeout(() => convertUTCToLocal(localField, utcValue), 100);
            }
          }}
        />
        <p className="text-xs text-sky-600 mt-1">Format: HHMM (24hr UTC)</p>
      </div>
    );
  };

  const crewField = (fieldKey, label, placeholder) =>
    field(fieldKey,
      <div>
        <label htmlFor={`${fieldKey}_display`} className={labelClasses}>{label}</label>
        <div className="relative">
          {/* Hidden field carries the crew UUID submitted to the backend */}
          <input type="hidden" {...register(fieldKey)} />
          {/* Visible field shows the human-readable name */}
          <input
            type="text"
            id={`${fieldKey}_display`}
            {...register(`${fieldKey}_display`)}
            className={`${inputFieldClasses} pr-12`}
            placeholder={placeholder}
            onChange={(e) => {
              setValue(`${fieldKey}_display`, e.target.value);
              // Clear the UUID when the name is edited — forces re-selection
              setValue(fieldKey, '');
              searchCrew(e.target.value, fieldKey);
            }}
            onFocus={() => {
              if (watch(`${fieldKey}_display`)) {
                searchCrew(watch(`${fieldKey}_display`), fieldKey);
              }
              setCrewPopup({ open: true, field: fieldKey });
            }}
            onBlur={() => {
              setTimeout(() => {
                if (crewPopup.field === fieldKey) {
                  setCrewSuggestions([]);
                }
              }, 150);
            }}
          />
          <button
            type="button"
            onClick={() => setCrewPopup({ open: true, field: fieldKey })}
            className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-50 rounded-r-lg transition-colors"
          >
            <User className="h-5 w-5 text-gray-400" />
          </button>

          {crewPopup.field === fieldKey && crewSuggestions.length > 0 && (
            <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto top-full mt-1">
              {searchingCrew ? (
                <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600 mr-2"></div> Searching crew...
                </div>
              ) : (
                crewSuggestions.map((crew) => (
                  <button
                    key={crew.id}
                    type="button"
                    onClick={() => selectCrew(crew)}
                    className="w-full text-left px-4 py-3 hover:bg-sky-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{crew.name}</div>
                    <div className="text-sm text-gray-600">
                      {crew.company && `${crew.company} • `}{crew.position} • {crew.certificate_type}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );

  const tripOptionCard = (value, title, description) => (
    <label className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all duration-200 ${
      tripOption === value ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
    }`}>
      <input type="radio" value={value} checked={tripOption === value} onChange={(e) => setTripOption(e.target.value)} className="sr-only" />
      <div className="flex items-center">
        <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
          tripOption === value ? 'border-sky-500 bg-sky-500' : 'border-gray-300'
        }`}>
          {tripOption === value && <div className="w-2 h-2 bg-white rounded-full"></div>}
        </div>
        <div>
          <div className="font-medium text-gray-900">{title}</div>
          <div className="text-sm text-gray-500">{description}</div>
        </div>
      </div>
    </label>
  );

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  const blockDiff = parseFloat(watch('block_diff'));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 rounded-lg bg-sky-600 shadow-md">
            <Plane className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{isEdit ? 'Edit Flight' : returnOf ? 'Log Return Leg' : 'Log New Flight'}</h1>
        </div>
        <p className="text-gray-600">
          {isEdit ? 'Update your flight details and flight time' : 'Record a new flight and its flight time'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

        {/* Trip Assignment (new flights only) */}
        {!isEdit && (
          <div className={cardClasses}>
            <h2 className={cardTitleClasses}>
              <Navigation className="h-6 w-6 mr-3 text-sky-600" /> Trip Assignment
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tripOptionCard('none', 'Individual Flight', 'No trip assignment')}
              {tripOptionCard('existing', 'Existing Trip', 'Assign to existing')}
              {tripOptionCard('new', 'New Trip', 'Create a new trip')}
            </div>

            {tripOption === 'existing' && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <label htmlFor="existing_trip_id" className={labelClasses}>Select Existing Trip</label>
                {loadingTrips ? (
                  <div className="px-4 py-3 border border-gray-300 rounded-lg text-gray-500 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600 mr-3"></div> Loading available trips...
                  </div>
                ) : (
                  <select
                    id="existing_trip_id"
                    {...register('existing_trip_id', { required: tripOption === 'existing' ? 'Please select an existing trip' : false })}
                    className={inputFieldClasses}
                  >
                    <option value="">Select a trip...</option>
                    {availableTrips.map((trip) => {
                      // Trip numbers repeat month to month; the start date tells them apart
                      const tripDate = trip.start_date ? parseISO(String(trip.start_date).slice(0, 10))
                        : trip.on_duty ? new Date(trip.on_duty) : null;
                      const formattedDate = (tripDate && !isNaN(tripDate.getTime()))
                        ? ` (${format(tripDate, 'MMM dd, yyyy')})`
                        : '';
                      return (
                        <option key={trip.id} value={trip.id}>
                          {trip.trip_number}{formattedDate}
                        </option>
                      );
                    })}
                  </select>
                )}
                {errors.existing_trip_id && <p className="mt-1 text-sm text-red-600">{errors.existing_trip_id.message}</p>}
              </div>
            )}

            {tripOption === 'new' && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <label htmlFor="new_trip_number" className={labelClasses}>New Trip Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  id="new_trip_number"
                  {...register('new_trip_number', { required: tripOption === 'new' ? 'A new trip number is required' : false })}
                  className={inputFieldClasses}
                  placeholder="e.g., TRIP-1001"
                />
                {errors.new_trip_number && <p className="mt-1 text-sm text-red-600">{errors.new_trip_number.message}</p>}
              </div>
            )}
          </div>
        )}

        {/* Flight Information Card */}
        <div className={cardClasses}>
          <h2 className={cardTitleClasses}>
            <Plane className="h-6 w-6 mr-3 text-sky-600" /> Basic Flight Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            {field('date',
              <div>
                <label htmlFor="date" className={labelClasses}>Date <span className="text-red-500">*</span></label>
                <input type="date" id="date" {...register('date', { required: 'Date is required' })} className={inputFieldClasses} />
                {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>}
              </div>
            )}

            {field('flight_number',
              <div>
                <label htmlFor="flight_number" className={labelClasses}>Flight Number</label>
                <input type="text" id="flight_number" {...register('flight_number')} className={inputFieldClasses} placeholder="e.g., AA1234" />
              </div>
            )}

            {field('aircraft_id',
              <div>
                <label htmlFor="aircraft_id" className={labelClasses}>Aircraft ID</label>
                <div className="relative">
                  <input
                    type="text"
                    id="aircraft_id"
                    {...register('aircraft_id')}
                    className={`${inputFieldClasses} pr-12 uppercase`}
                    placeholder="e.g., N123AB"
                    onChange={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                      setValue('aircraft_id', e.target.value);
                      searchAircraft(e.target.value);
                    }}
                    onFocus={() => {
                      if (watch('aircraft_id')) {
                        searchAircraft(watch('aircraft_id'));
                      }
                      setAircraftPopup(true);
                    }}
                    onBlur={() => { setTimeout(() => setAircraftPopup(false), 150); }}
                  />
                  <button
                    type="button"
                    onClick={() => setAircraftPopup(true)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-50 rounded-r-lg transition-colors"
                  >
                    <Search className="h-5 w-5 text-gray-400" />
                  </button>

                  {aircraftPopup && aircraftSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto top-full mt-1">
                      {searchingAircraft ? (
                        <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600 mr-2"></div> Searching aircraft...
                        </div>
                      ) : (
                        aircraftSuggestions.map((aircraft) => (
                          <button
                            key={aircraft.aircraft_id}
                            type="button"
                            onClick={() => selectAircraft(aircraft)}
                            className="w-full text-left px-4 py-3 hover:bg-sky-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{aircraft.aircraft_id}</div>
                            <div className="text-sm text-gray-600">{aircraft.aircraft_type} • {aircraft.manufacturer} {aircraft.model}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {errors.aircraft_id && <p className="mt-1 text-sm text-red-600">{errors.aircraft_id.message}</p>}
              </div>
            )}

            {field('aircraft_type',
              <div>
                <label htmlFor="aircraft_type" className={labelClasses}>Aircraft Type</label>
                <input
                  type="text"
                  id="aircraft_type"
                  {...register('aircraft_type')}
                  className={`${inputFieldClasses} bg-gray-100 text-gray-700 cursor-not-allowed`}
                  readOnly
                  placeholder="C172 / B737"
                />
              </div>
            )}

            {field('departure_airport',
              <div>
                <label htmlFor="departure_airport" className={labelClasses}>Departure Airport <span className="text-red-500">*</span></label>
                <input type="text" id="departure_airport" {...register('departure_airport', { required: 'Departure airport is required' })} className={`${inputFieldClasses} uppercase`} placeholder="e.g., KJFK" maxLength={4} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); }} />
                {errors.departure_airport && <p className="mt-1 text-sm text-red-600">{errors.departure_airport.message}</p>}
              </div>
            )}

            {field('arrival_airport',
              <div>
                <label htmlFor="arrival_airport" className={labelClasses}>Arrival Airport <span className="text-red-500">*</span></label>
                <input type="text" id="arrival_airport" {...register('arrival_airport', { required: 'Arrival airport is required' })} className={`${inputFieldClasses} uppercase`} placeholder="e.g., KLAX" maxLength={4} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); }} />
                {errors.arrival_airport && <p className="mt-1 text-sm text-red-600">{errors.arrival_airport.message}</p>}
              </div>
            )}

          </div>
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setShowAddAircraftModal(true)}
              className="inline-flex items-center px-6 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" /> Add New Aircraft
            </button>
          </div>
        </div>

        {/* Flight Times Section */}
        <div className={cardClasses}>
          <h2 className={cardTitleClasses}>
            <Clock className="h-6 w-6 mr-3 text-sky-600" /> Flight Times
          </h2>

          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Scheduled Times</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {field('scheduled_out',
                <div>
                  <label htmlFor="scheduled_out" className={labelClasses}>Scheduled Out (Local)</label>
                  <input type="number" id="scheduled_out" {...register('scheduled_out')} className={inputFieldClasses} placeholder="0800" maxLength="4" />
                  <p className="text-xs text-gray-500 mt-1">Format: HHMM (24hr Local)</p>
                </div>
              )}
              {field('scheduled_in',
                <div>
                  <label htmlFor="scheduled_in" className={labelClasses}>Scheduled In (Local)</label>
                  <input type="number" id="scheduled_in" {...register('scheduled_in')} className={inputFieldClasses} placeholder="1200" maxLength="4" />
                  <p className="text-xs text-gray-500 mt-1">Format: HHMM (24hr Local)</p>
                </div>
              )}
              {field('scheduled_block',
                <div>
                  <label htmlFor="scheduled_block" className={labelClasses}>Scheduled Block</label>
                  <div className="relative">
                    <input type="number" step="0.1" id="scheduled_block" {...register('scheduled_block')} className={inputFieldClasses} placeholder="4.0" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded">AUTO</div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Hours — auto-calculated (timezone aware)</p>
                </div>
              )}
              {field('block_diff',
                <div>
                  <label htmlFor="block_diff" className={labelClasses}>Block Diff</label>
                  <input
                    type="number"
                    step="0.1"
                    id="block_diff"
                    {...register('block_diff')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 font-medium"
                    placeholder="0.0"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">Hours (e.g., 1.5)</p>
                    {watch('block_diff') && (
                      <span className={`text-xs font-medium flex items-center ${
                        blockDiff < 0 ? 'text-red-600' : blockDiff > 0 ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {blockDiff < 0 && <>😤 Underblocked</>}
                        {blockDiff > 0 && <>🍻 Overblocked</>}
                        {blockDiff === 0 && <>🎯 On Time</>}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8">
            <h3 className="text-lg font-medium text-gray-700 mb-6">Actual Times (Local Entry)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {localTimeField('actual_out', 'Out Time', '0800')}
              {localTimeField('actual_off', 'Off Time', '0815')}
              {localTimeField('actual_on', 'On Time', '1025')}
              {localTimeField('actual_in', 'In Time', '1035')}
            </div>
          </div>

          <div className="mt-8 p-4 bg-sky-50 rounded-lg border border-sky-200">
            <h4 className="text-md font-semibold text-sky-800 mb-4">UTC Time Entry</h4>
            <p className="text-sm text-sky-700 mb-4">Enter times in UTC to automatically convert to local time above.</p>

            <AcarsPhotoScanner onApply={handleAcarsTimesApplied} />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {utcTimeField('actual_out', 'UTC Out Time', '0800')}
              {utcTimeField('actual_off', 'UTC Off Time', '0815')}
              {utcTimeField('actual_on', 'UTC On Time', '1025')}
              {utcTimeField('actual_in', 'UTC In Time', '1035')}
            </div>
          </div>
        </div>

        {/* Pilot/Flight Time Breakdown Card */}
        <div className={cardClasses}>
          <h2 className={cardTitleClasses}>
            <Target className="h-6 w-6 mr-3 text-sky-600" /> Pilot & Flight Time Breakdown
          </h2>

          {field('pilot_flying',
            <div className="mb-6 p-4 bg-sky-50 rounded-lg border border-sky-200">
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input type="checkbox" {...register('pilot_flying')} className="sr-only" />
                  <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                    pilotFlying ? 'bg-sky-600' : 'bg-gray-300'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 shadow-md ${
                      pilotFlying ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">Pilot Flying</span>
                <span className={`ml-4 text-sm font-bold ${pilotFlying ? 'text-sky-600' : 'text-orange-500'}`}>
                  {pilotFlying ? 'PILOT FLYING' : 'PILOT MONITORING'}
                </span>
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {field('total_time',
              <div>
                <label htmlFor="total_time" className={labelClasses}>Total Time (Auto)</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    id="total_time"
                    {...register('total_time')}
                    className={`${inputFieldClasses} bg-gray-100 text-gray-700 font-medium cursor-not-allowed`}
                    placeholder="3.9"
                    readOnly
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <div className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded">AUTO</div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from Out/In times</p>
              </div>
            )}
            {numberField('pic_time', 'PIC Time')}
            {numberField('sic_time', 'SIC Time')}
            {numberField('night_time', 'Night Time')}
            {numberField('cross_country_time', 'Cross Country Time')}
            {numberField('actual_instrument', 'Actual IMC')}
            {numberField('simulated_instrument', 'Simulated Instrument')}
            {numberField('simulator', 'SIM/FTD Time')}
            {numberField('amel_time', 'Multi-Engine Time')}
            {numberField('turbine_time', 'Turbine Time')}
            {numberField('turbo_prop_time', 'Turbo-Prop Time')}
            {numberField('turbo_fan_time', 'Turbo-Fan Time')}
            {numberField('asel_time', 'ASEL Time')}
            {numberField('ases_time', 'ASES Time')}
            {numberField('ames_time', 'AMES Time')}
            {numberField('dual_received', 'Dual Received')}
            {numberField('dual_given', 'Dual Given')}
          </div>
        </div>

        {/* Takeoffs, Landings & Remarks Card */}
        <div className={cardClasses}>
          <h2 className={cardTitleClasses}>
            <FileText className="h-6 w-6 mr-3 text-sky-600" /> Additional Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {numberField('day_takeoffs', 'Takeoffs Day', { placeholder: '0', step: undefined })}
            {numberField('day_landings', 'Landings Day', { placeholder: '0', step: undefined })}
            {numberField('night_takeoffs', 'Takeoffs Night', { placeholder: '0', step: undefined })}
            {numberField('night_landings', 'Landings Night', { placeholder: '0', step: undefined })}
          </div>

          {field('remarks',
            <div className="mt-8 border-t pt-6 border-gray-200">
              <label htmlFor="remarks" className={labelClasses}>Remarks</label>
              <textarea
                id="remarks"
                {...register('remarks')}
                rows={4}
                className={`${inputFieldClasses} resize-none`}
                placeholder="Notes on weather, training, specific flight events..."
              />
            </div>
          )}
        </div>

        {/* Approaches Card */}
        <div className={cardClasses}>
          <h2 className={cardTitleClasses}>
            <Target className="h-6 w-6 mr-3 text-sky-600" /> Approaches
          </h2>

          {[1, 2].map(n => (
            <div key={n}>
              <h3 className={`text-lg font-medium text-gray-700 mb-4 ${n === 2 ? 'mt-8 border-t pt-6 border-gray-200' : ''}`}>Approach {n}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {numberField(`approach_${n}_number`, 'Number', { placeholder: '1', step: undefined })}
                {field(`approach_${n}_type`,
                  <div>
                    <label htmlFor={`approach_${n}_type`} className={labelClasses}>Type</label>
                    <input type="text" id={`approach_${n}_type`} {...register(`approach_${n}_type`)} className={inputFieldClasses} placeholder="ILS / RNAV" />
                  </div>
                )}
                {field(`approach_${n}_runway`,
                  <div>
                    <label htmlFor={`approach_${n}_runway`} className={labelClasses}>Runway</label>
                    <input type="text" id={`approach_${n}_runway`} {...register(`approach_${n}_runway`)} className={inputFieldClasses} placeholder={n === 1 ? '04R' : '22L'} />
                  </div>
                )}
                {n === 1 && field('cat_iii',
                  <div className="col-span-1">
                    <label htmlFor="cat_iii" className={labelClasses}>Cat III Approach?</label>
                    <div className="mt-3">
                      <label className="inline-flex items-center">
                        <input type="checkbox" id="cat_iii" {...register('cat_iii')} className="form-checkbox h-5 w-5 text-sky-600 rounded" />
                        <span className="ml-2 text-gray-700">Yes</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Crew Information Card */}
        <div className={cardClasses}>
          <h2 className={cardTitleClasses}>
            <Users className="h-6 w-6 mr-3 text-sky-600" /> Crew & Personnel
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {crewField('pic_crew', 'PIC/Captain Crew', 'Captain Name')}
            {crewField('sic_crew', 'SIC/P2 Crew', 'First Officer Name')}
          </div>

          <div className="flex justify-center space-x-4 mt-8">
            <button
              type="button"
              onClick={() => openCrewModal('pic_crew')}
              className="inline-flex items-center px-6 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" /> Add PIC Crew
            </button>
            <button
              type="button"
              onClick={() => openCrewModal('sic_crew')}
              className="inline-flex items-center px-6 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" /> Add SIC Crew
            </button>
          </div>
        </div>

        {/* Form Submission Footer - sticky so actions stay on screen */}
        <div className="sticky bottom-0 z-40 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-gray-50/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex flex-nowrap items-center justify-end gap-2 sm:gap-3">

          <button
            type="button"
            onClick={isCustomizingFields ? exitCustomizeMode : enterCustomizeMode}
            title={isCustomizingFields ? 'Exit Edit Mode' : 'Edit Fields'}
            aria-label={isCustomizingFields ? 'Exit Edit Mode' : 'Edit Fields'}
            className={`shrink-0 inline-flex items-center px-3 sm:px-4 py-3 border rounded-lg transition-colors shadow-sm ${
              isCustomizingFields
                ? 'bg-orange-100 border-orange-300 hover:bg-orange-200 text-orange-700'
                : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Settings className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">{isCustomizingFields ? 'Exit Edit Mode' : 'Edit Fields'}</span>
          </button>

          {isCustomizingFields && (
            <button
              type="button"
              onClick={saveFieldSettings}
              disabled={loadingFieldSettings}
              title="Save Field Settings"
              aria-label="Save Field Settings"
              className="shrink-0 inline-flex items-center px-3 sm:px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors shadow-sm"
            >
              {loadingFieldSettings ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 sm:h-4 sm:w-4 border-b-2 border-white sm:mr-2"></div>
                  <span className="hidden sm:inline">Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Save Field Settings</span>
                </>
              )}
            </button>
          )}

          {isEdit && (
            <button
              type="button"
              onClick={() => navigate(`/add-flight?returnOf=${id}`)}
              title="Log return leg"
              aria-label="Log return leg"
              className="shrink-0 inline-flex items-center px-3 sm:px-4 py-3 border border-gray-300 bg-white hover:bg-gray-50 text-sky-700 rounded-lg transition-colors shadow-sm"
            >
              <ArrowLeftRight className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Return Leg</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate('/flights')}
            className="flex-1 sm:flex-none whitespace-nowrap px-4 sm:px-8 py-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center shadow-sm"
          >
            <X className="h-5 w-5 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 sm:flex-none whitespace-nowrap px-4 sm:px-8 py-3 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white font-medium rounded-lg focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center sm:min-w-[160px] shadow-md"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {isEdit ? 'Updating...' : 'Adding Flight...'}
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                {isEdit ? 'Update Flight' : 'Add Flight'}
              </>
            )}
          </button>
        </div>
      </form>

      <AddAircraftModal
        isOpen={showAddAircraftModal}
        onClose={() => setShowAddAircraftModal(false)}
        onAircraftAdded={handleAircraftAdded}
      />

      <AddCrewModal
        isOpen={showAddCrewModal}
        onClose={() => {
          setShowAddCrewModal(false);
          setCrewModalTargetField(null);
        }}
        onCrewAdded={handleCrewAdded}
        targetField={crewModalTargetField}
      />
    </div>
  );
};

export default FlightForm;
